/**
 * La cabecera de la home NUEVA (grupo C): tres cards por DESTINO —"Redes sociales",
 * "Catálogos" y "Anuncios"— con un abanico de resultados sellados con su plataforma
 * (ADR-2026-09-29-la-home-se-parte-por-destino, supersede las dos cards de vertiente
 * "Crear imágenes" / "Crear videos"). No navegan: abren un popup con los modos de esa
 * sección, así volver atrás es cerrar un diálogo y no rearmar el layout
 * (ADR-2026-08-31-version2-novedades-arriba-y-popup-por-vertiente). El Agente no vive
 * acá: ni la barra que lo presentaba ni el hero de los rubros sin catálogo propio.
 *
 * Vivía adentro de `HomeMinimal`; salió a su archivo cuando las dos cabeceras pasaron a
 * convivir (ADR-2026-10-07-la-home-elige-su-cabecera): ésta para el grupo C,
 * `CabeceraPorVertiente` para el resto. `HomeMinimal` pone el saludo, los accesos, los
 * banners y todo lo de abajo; acá va sólo "Elegí qué querés crear".
 */
import { Fragment, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Clapperboard, Image as ImageIcon, X } from 'lucide-react';
import { setupAutoplayVideo } from '../v2/components/HomeCards';
import type { ModoHome } from './EstiloMinimal';
// La grilla de modos del popup (la misma que dibuja la solapa "Herramientas" del hero
// del Agente en el teléfono).
import { GrillaDeModos } from '../v2/components/GrillaDeModos';
import { portadaDeRubro } from '../v2/modesCatalog';
import { CARATULAS_CON_TINTE, caratulaConTinte, columnasDelPopup, modoPrincipalDeLaSeccion, modosDeCatalogoDelRubro, PROXIMOS_DE_CATALOGO, modosDeRedesDelRubro, proximosQueQuedan, tieneCaratulaDelRubro } from './seccionesDeModos';
import { CardModoProximo } from './CardModoProximo';
import { palabraDeModeloActual } from '../../lib/nombreDeModelo';
import { RUBRO_BELLEZA, RUBRO_HOGAR_DECO } from '../../lib/rubro';
import { RUBRO_GASTRONOMIA } from '../v2/modes/redes/contenidoPorRubro';
import { DESTINOS_VISIBLES } from '../v2/components/ordenDeDestinos';
import { RUTA_REDES } from '../v2/modes/redes/redes';
import { miniatura } from '../../utils/miniatura';

export type Seccion = 'redes' | 'catalogos' | 'anuncios';

/*
 * Qué modo va en "Catálogos" y en "Redes sociales" sale de `seccionesDeModos`, la MISMA
 * lista que usan los paneles de la sidebar (ADR-2026-10-05-la-sidebar-por-secciones). Antes
 * vivía acá (ADR-2026-09-29-la-home-se-parte-por-destino) y la sidebar no la veía.
 */

/**
 * Modos que la home NO muestra en ninguna sección, aunque existan. Hoy ninguno: Catálogo
 * (y su variante de muebles) estuvo oculto y volvió al popup de Catálogos con otro nombre,
 * "Generación en lote" (ADR-2026-10-05-la-home-ofrece-crear-o-copiar-contenido). La lista
 * queda para la próxima vez que haga falta esconder uno.
 */
const MODOS_OCULTOS_EN_LA_HOME = new Set<string>();


/**
 * Una pieza del abanico: un RESULTADO (foto o clip de `public/images/`) con el logo de la
 * plataforma donde terminaría como sello en la esquina (`public/images/logos_plataformas/`).
 * Antes eran sólo los logos y la card se leía como "conectá tu cuenta", no como "creá".
 */
export interface Plataforma {
  nombre: string;
  logo: string;
  /** Lo que se ve en la ficha. Video: loop silenciado. */
  pieza: { foto?: string; video?: string };
  /**
   * Zoom del logo dentro de su cuadrado. Instagram trae sus propias esquinas redondeadas
   * sobre fondo blanco: sin agrandarlo un poco, las puntas blancas asoman por el recorte.
   */
  zoom?: number;
}

/**
 * Copy, tinte y plataformas de las tres cards. El orden de las plataformas es el del
 * abanico: la primera queda ATRÁS y la última al frente.
 */
export const SECCIONES: Record<
  Seccion,
  {
    titulo: string;
    bajada: string;
    /** Qué sale de esta sección: los chips de formato debajo de la bajada. */
    formatos: ('imagen' | 'video')[];
    tituloPopup: string;
    /** Fondo de la card. Los tres tintes tienen la misma luminosidad: ninguno se adelanta. */
    tinte: string;
    /**
     * Fondo de la flecha cuando se apunta a la card (la flecha pasa a blanco): un color
     * oscuro y saturado de la sección. Clase entera, no armada, para que Tailwind la genere.
     */
    flechaHover: string;
    plataformas: Plataforma[];
    /** Todavía no tiene modos: la card se ve pero no abre nada. */
    proximamente?: boolean;
    /**
     * La card NAVEGA a una pantalla en vez de abrir el popup de modos. Hoy ninguna la usa:
     * Redes sociales iba derecho al modo Redes y ahora da a elegir entre crear y copiar
     * (ADR-2026-10-05-la-home-ofrece-crear-o-copiar-contenido).
     */
    ruta?: string;
    /**
     * Modos que todavía no existen y se anuncian en el popup, al final de la grilla,
     * como cards de "Próximamente" que no se abren. Con `path`, el anuncio se va cuando ese
     * modo ya está en la lista (`proximosDe`).
     */
    modosProximos?: { nombre: string; bajada: string; path?: string }[];
    /**
     * Las carátulas del popup con el fondo del tinte de la sección en vez del lavanda de
     * siempre (ADR-2026-10-05-caratulas-con-el-tinte-de-su-seccion). Son las mismas
     * carátulas recoloreadas con `scripts/caratulas-con-tinte/recolorear.py`. Una portada
     * del rubro gana: es otra foto, no la misma con otro fondo.
     */
    caratulas?: Readonly<Record<string, string>>;
  }
> = {
  redes: {
    titulo: 'Redes sociales',
    bajada: 'Creá posteos, carruseles y videos para publicar.',
    formatos: ['imagen', 'video'],
    tituloPopup: '¿Cómo querés arrancar?',
    tinte: 'bg-[#F2ECF5]',
    // El violeta de los íconos de la card (#7A4A96), saturado: como Catálogos y Anuncios, el
    // hover de la flecha es del color de su tarjeta (ADR-2026-10-15-la-flecha-de-redes-en-violeta).
    flechaHover: 'group-hover:bg-[#8236B8]',
    plataformas: [
      { nombre: 'Instagram', logo: '/images/logos_plataformas/instagram.png', zoom: 1.1, pieza: { foto: '/images/ejemplos_modos/personalizada/personalizada-4.jpg' } },
      { nombre: 'Facebook', logo: '/images/logos_plataformas/facebook.png', pieza: { foto: '/images/ejemplos_modos/automatico/automatico-3.jpg' } },
      // La del frente, de moda: el clip del abrigo del onboarding de Moda, achicado a 480 px
      // (ADR-2026-10-12-el-video-de-redes-es-de-moda).
      { nombre: 'TikTok', logo: '/images/logos_plataformas/tiktok.png', pieza: { video: '/images/home_vertientes/redes_moda.mp4' } },
    ],
    caratulas: CARATULAS_CON_TINTE.redes,
  },
  catalogos: {
    titulo: 'Catálogos',
    get bajada() {
      return `Creá fotos y videos de producto y con ${palabraDeModeloActual().plural} para tu tienda.`;
    },
    formatos: ['imagen', 'video'],
    tituloPopup: '¿Qué querés crear para tu catálogo?',
    tinte: 'bg-[#F4F1E6]',
    flechaHover: 'group-hover:bg-[#A9873D]',
    plataformas: [
      { nombre: 'Shopify', logo: '/images/logos_plataformas/shopify.png', pieza: { foto: '/images/ejemplos_modos/catalogo/catalogo-2.jpg' } },
      { nombre: 'Mercado Libre', logo: '/images/logos_plataformas/mercadolibre.png', pieza: { foto: '/images/ejemplos_modos/catalogo/catalogo-6.jpg' } },
      // La del frente, en movimiento: la modelo de la foto de al lado, de cuerpo entero.
      { nombre: 'Tiendanube', logo: '/images/logos_plataformas/tiendanube.png', pieza: { video: '/images/home_vertientes/catalogo_moda.mp4' } },
    ],
    modosProximos: [...PROXIMOS_DE_CATALOGO],
    caratulas: CARATULAS_CON_TINTE.catalogo,
  },
  anuncios: {
    titulo: 'Anuncios',
    bajada: 'Creá piezas para tus campañas en Meta y Google.',
    // Imagen y Video, como las otras dos (ADR-2026-10-12-anuncios-tambien-video).
    formatos: ['imagen', 'video'],
    tituloPopup: '¿Qué anuncio querés crear?',
    tinte: 'bg-[#EDEFF5]',
    flechaHover: 'group-hover:bg-[#4A5D8F]',
    plataformas: [
      { nombre: 'Google Ads', logo: '/images/logos_plataformas/google-ads.png', pieza: { foto: '/images/ejemplos_modos/anuncio/anuncio-1.jpg' } },
      { nombre: 'Meta', logo: '/images/logos_plataformas/meta.png', pieza: { foto: '/images/ejemplos_modos/anuncio/anuncio-3.jpg' } },
    ],
    proximamente: true,
  },
};

/**
 * Las fotos del abanico de una card cambian con el rubro: Belleza ve productos de belleza en
 * vez de ropa (ADR-2026-10-12-el-abanico-de-belleza). Sólo cambia lo que se ve en la ficha;
 * la plataforma y su sello son los mismos. Van en el orden de `plataformas` (atrás → frente).
 */
const PIEZAS_POR_RUBRO: Readonly<Record<string, Partial<Record<Seccion, Plataforma['pieza'][]>>>> = {
  [RUBRO_BELLEZA]: {
    redes: [
      { foto: '/images/onboarding/vitrina/belleza-mascara-redes.jpg' },
      { foto: '/images/onboarding/rubros/belleza/carousel/step-4.jpg' },
      // El labial del onboarding de Belleza, achicado a 480 px como el de Moda.
      { video: '/images/home_vertientes/redes_belleza.mp4' },
    ],
    catalogos: [
      // La crema y no el rímel: el clip del frente ya es de rímel
      // (ADR-2026-10-12-el-abanico-de-belleza-sin-dos-rimel).
      { foto: '/images/onboarding/rubros/belleza/carousel/step-2.jpg' },
      { foto: '/images/onboarding/rubros/belleza/lapiz/original.jpg' },
      // El rímel del onboarding de Belleza, achicado a 480 px como el sweater de Moda.
      { video: '/images/home_vertientes/catalogo_belleza.mp4' },
    ],
    // Las piezas de anuncio que subió Maxi, recortadas a 4:5 (el formato de la ficha) con el
    // título y el producto adentro.
    anuncios: [
      { foto: '/images/ejemplos_modos/anuncio/anuncio-belleza-labial.jpg' },
      { foto: '/images/ejemplos_modos/anuncio/anuncio-belleza-polvo.jpg' },
    ],
  },
  // Hogar y Decoración: muebles de su onboarding y del modo de vistas
  // (ADR-2026-10-12-el-abanico-de-hogar). Anuncios, con las dos piezas de muebles que subió
  // Maxi (ADR-2026-10-12-anuncios-de-hogar).
  [RUBRO_HOGAR_DECO]: {
    redes: [
      { foto: '/images/onboarding/rubros/hogar/carousel/step-4.jpg' },
      { foto: '/images/onboarding/rubros/hogar/carousel/step-2.jpg' },
      // La silla del onboarding de Hogar, achicada a 480 px como la de Moda.
      { video: '/images/home_vertientes/redes_hogar.mp4' },
    ],
    catalogos: [
      { foto: '/images/home_vertientes/catalogo_hogar_contexto.jpg' },
      { foto: '/images/onboarding/rubros/hogar/silla/fondo-blanco.jpg' },
      // La mesa de luz del onboarding de Hogar, achicada a 480 px.
      { video: '/images/home_vertientes/catalogo_hogar.mp4' },
    ],
    // Recortadas de 9:16 a 4:5 (el formato de la ficha) con el título y el mueble adentro,
    // como las de Belleza: la cómoda va con Google Ads y el sillón con Meta.
    anuncios: [
      { foto: '/images/ejemplos_modos/anuncio/anuncio-hogar-comoda.jpg' },
      { foto: '/images/ejemplos_modos/anuncio/anuncio-hogar-sillon.jpg' },
    ],
  },
  // Gastronomía: platos de su onboarding y los clips que ya usa Crear, achicados a 480 px
  // (ADR-2026-10-12-el-abanico-de-gastronomia).
  [RUBRO_GASTRONOMIA]: {
    // Sin una foto repetida entre las dos cards (ADR-2026-10-12-el-abanico-de-gastronomia-3):
    // Redes en ambiente (la pizza en la mesa, la hamburguesa en la calle, el clip del helado)…
    redes: [
      { foto: '/images/onboarding/rubros/gastronomia/pizza/redes.jpg' },
      { foto: '/images/onboarding/rubros/gastronomia/hamburguesa/redes.jpg' },
      { video: '/images/crear_rubros/gastronomia/helado.mp4' },
    ],
    // …y Catálogos en foto de producto (pizza y hamburguesa sobre fondo blanco) con el clip
    // del sushi al frente.
    catalogos: [
      { foto: '/images/onboarding/rubros/gastronomia/pizza/fondo-blanco.jpg' },
      { foto: '/images/onboarding/rubros/gastronomia/hamburguesa/fondo-blanco.jpg' },
      { video: '/images/crear_rubros/gastronomia/sushi.mp4' },
    ],
    // Las piezas de anuncio que subió Maxi (ADR-2026-10-12-anuncios-de-gastronomia), de 9:16 a
    // 4:5: la milanesa con Google Ads y el helado con Meta.
    anuncios: [
      { foto: '/images/ejemplos_modos/anuncio/anuncio-gastronomia-milanesa.jpg' },
      { foto: '/images/ejemplos_modos/anuncio/anuncio-gastronomia-helado.jpg' },
    ],
  },
};

/** Las plataformas de una card con las fotos de su rubro, si tiene propias. */
export function plataformasDe(seccion: Seccion, rubro?: string | null): Plataforma[] {
  const base = SECCIONES[seccion].plataformas;
  const piezas = rubro ? PIEZAS_POR_RUBRO[rubro]?.[seccion] : undefined;
  return piezas ? base.map((p, i) => (piezas[i] ? { ...p, pieza: piezas[i] } : p)) : base;
}

/**
 * Dónde cae cada ficha del abanico, de la de ATRÁS a la del frente: desplazamiento (x, y)
 * en unidades del abanico (`--u`, ver `AbanicoDeResultados`) y giro en grados. Se pisan y
 * cada una gira para su lado, como fotos apoyadas sobre la card. `hover` es cómo quedan
 * al pasar el mouse: se abren un poco, para que la card responda sin moverse de lugar.
 */
type Pose = { x: number; y: number; r: number };
const POSICIONES_DEL_ABANICO: Record<2 | 3, { reposo: Pose; hover: Pose }[]> = {
  2: [
    { reposo: { x: -46, y: 8, r: -8 }, hover: { x: -56, y: 6, r: -12 } },
    { reposo: { x: 42, y: -6, r: 6 }, hover: { x: 52, y: -10, r: 9 } },
  ],
  3: [
    { reposo: { x: -80, y: 12, r: -10 }, hover: { x: -94, y: 12, r: -14 } },
    { reposo: { x: 80, y: 10, r: 8 }, hover: { x: 94, y: 10, r: 12 } },
    { reposo: { x: 0, y: -6, r: -2 }, hover: { x: 0, y: -14, r: 0 } },
  ],
};

const transformDe = ({ x, y, r }: Pose) => `translate(calc(${x} * var(--u)), calc(${y} * var(--u))) rotate(${r}deg)`;

/**
 * El primer cuadro de un clip de la ficha: un `.jpg` al lado del `.mp4`, con el mismo nombre
 * (como los de `crear_rubros/`). Se ve apenas carga la home y el clip arranca encima cuando
 * llega (ADR-2026-10-18-las-fichas-de-la-home-y-crear-usan-miniaturas).
 */
export const posterDeClip = (video: string): string => video.replace(/\.mp4$/, '.jpg');

/**
 * Una ficha: el resultado (foto o clip en loop) que ocupa toda la ficha. La foto va en su
 * miniatura de 420 px: la ficha mide menos de 150 px y el original pesa hasta 400 KB.
 */
function PiezaDeResultado({ pieza }: { pieza: Plataforma['pieza'] }) {
  if (pieza.video) {
    return (
      <video
        ref={setupAutoplayVideo}
        src={pieza.video}
        poster={posterDeClip(pieza.video)}
        muted
        loop
        playsInline
        autoPlay
        preload="metadata"
        className="h-full w-full object-cover"
      />
    );
  }
  return <img src={pieza.foto ? miniatura(pieza.foto) : undefined} alt="" loading="lazy" className="h-full w-full object-cover" />;
}

/**
 * El abanico de una card: RESULTADOS en fichas verticales, giradas y superpuestas, cada
 * una con el logo de su plataforma como sello en la esquina. Muestra lo que se crea
 * (fotos, videos) y dónde termina; con sólo los logos, la card se leía como una
 * integración y no como un acceso para crear. La del frente es la del medio.
 *
 * `group-hover` y no estado propio: el hover es de la card entera, no del abanico.
 */
export function AbanicoDeResultados({
  plataformas,
  cantidad = 3,
  unidad,
  menor = false,
}: {
  plataformas: Plataforma[];
  /** Cuántas fichas: las ÚLTIMAS `cantidad` (las del frente). La card del teléfono usa dos. */
  cantidad?: 2 | 3;
  /** Pisa `--u` (la unidad del abanico) para dibujarlo a otra escala. */
  unidad?: string;
  /** Desde `lg`, al 80 %: con dos cards por fila (más anchas) las fichas crecían de más
   *  (ADR-2026-10-26-con-dos-destinos-el-abanico-mas-chico). Debajo de `lg` no cambia. */
  menor?: boolean;
}) {
  const lista = plataformas.slice(-cantidad);
  const posiciones = POSICIONES_DEL_ABANICO[lista.length === 3 ? 3 : 2];
  return (
    // `--u` es la unidad del abanico: 1px en la card de alto fijo (debajo de `lg`) y, desde
    // `lg`, una fracción del ANCHO de la card (`cqw`, la card es el contenedor): las cards
    // son cuadradas y crecen con la pantalla, y con medidas fijas el abanico quedaba chico
    // en el medio de una card grande. Todo —fichas, sellos, desplazamientos— va en `--u`,
    // así crece en proporción. `cqw` es el ancho de CONTENIDO (sin el `p-6`). Dos topes:
    // con /300 las fichas de las puntas no llegan al borde de la card, y como la card es
    // cuadrada, el alto libre arriba del texto (~135px menos que ese ancho) limita en las
    // cards angostas, donde si no los sellos pisan el título. Piso de 0.8px: más chico no se lee.
  // 165 y no 135 desde que los formatos van arriba como stickers
  // (ADR-2026-10-09-los-formatos-de-la-card-por-destino-van-como-stickers): le quitan alto.
    <span
      aria-hidden="true"
      className={`relative block h-[calc(140*var(--u))] w-[calc(290*var(--u))] [--u:1px] ${
        menor
          ? 'lg:[--u:max(0.8px,min(calc(100cqw_/_375),calc((100cqw_-_165px)_/_200)))]'
          : 'lg:[--u:max(0.8px,min(calc(100cqw_/_300),calc((100cqw_-_165px)_/_160)))]'
      }`}
      style={unidad ? ({ '--u': unidad } as CSSProperties) : undefined}
    >
      {lista.map((p, i) => (
        <span
          key={p.nombre}
          className="absolute left-1/2 top-1/2 -ml-[calc(46*var(--u))] -mt-[calc(58*var(--u))] h-[calc(116*var(--u))] w-[calc(92*var(--u))] transition-transform duration-500 ease-out [transform:var(--reposo)] group-hover:[transform:var(--hover)]"
          style={
            {
              '--reposo': transformDe(posiciones[i].reposo),
              '--hover': transformDe(posiciones[i].hover),
            } as CSSProperties
          }
        >
          {/* El marco va en un span aparte del que gira: el borde blanco y la sombra son
              de la ficha, y así el `overflow-hidden` recorta la foto y no el sello. */}
          <span className="block h-full w-full overflow-hidden rounded-[calc(14*var(--u))] bg-white shadow-[0_10px_28px_rgba(16,24,45,0.18)] ring-[3px] ring-white">
            <PiezaDeResultado pieza={p.pieza} />
          </span>
          {/* El sello va del lado que queda a la vista: la ficha de la izquierda lo
              lleva a la izquierda, porque su esquina derecha la tapa la del medio. */}
          <span
            className={`absolute -bottom-[calc(9*var(--u))] block h-[calc(34*var(--u))] w-[calc(34*var(--u))] overflow-hidden rounded-[calc(10*var(--u))] bg-white shadow-[0_4px_10px_rgba(16,24,45,0.18)] ring-2 ring-white ${
              i === 0 ? '-left-[calc(9*var(--u))]' : '-right-[calc(9*var(--u))]'
            }`}
          >
            <img
              src={p.logo}
              alt=""
              className="h-full w-full object-cover"
              style={p.zoom ? { transform: `scale(${p.zoom})` } : undefined}
            />
          </span>
        </span>
      ))}
    </span>
  );
}

/** Los formatos: lo que sale de la sección, de un vistazo. */
export const CHIP_DE_FORMATO = {
  imagen: { rotulo: 'Imagen', Icono: ImageIcon },
  video: { rotulo: 'Video', Icono: Clapperboard },
} as const;

/** Los colores de la pastilla de formatos, del tinte de su card: el borde, un poco más oscuro
 *  que el fondo; los íconos, bien oscuros. */
const COLORES_DE_FORMATOS: Record<Seccion, { borde: string; icono: string }> = {
  redes: { borde: '#E2D3EC', icono: '#7A4A96' },
  catalogos: { borde: '#E6DDC2', icono: '#86692A' },
  anuncios: { borde: '#D8DDEB', icono: '#4A5D8F' },
};

/**
 * Los formatos de la card por destino: UNA pastilla blanca con Imagen y Video separados por una
 * línea; el borde y los íconos, del tinte de la card
 * (ADR-2026-10-14-los-formatos-de-la-card-van-en-una-pastilla). Plana y derecha, como la
 * flecha y "Próximamente": los stickers inclinados y llenos de color de antes no conectaban
 * con la card. La card del teléfono la usa en `chica`.
 */
export function FormatosDeLaCard({
  formatos,
  seccion,
  chica = false,
}: {
  formatos: readonly ('imagen' | 'video')[];
  seccion: Seccion;
  chica?: boolean;
}) {
  const { borde, icono } = COLORES_DE_FORMATOS[seccion];
  return (
    <span
      className={`inline-flex items-center rounded-[8px] bg-white font-semibold text-black/75 ${
        chica ? 'h-6 px-0.5 text-[11.5px]' : 'h-8 px-1 text-[13px]'
      }`}
      // Borde por dentro (no suma alto), de 1px como el de la card grande.
      style={{ boxShadow: `inset 0 0 0 1px ${borde}` }}
    >
      {formatos.map((f, i) => {
        const { rotulo, Icono } = CHIP_DE_FORMATO[f];
        return (
          <Fragment key={f}>
            {i > 0 && <span aria-hidden="true" className={`w-px ${chica ? 'h-3' : 'mx-0.5 h-3.5'}`} style={{ background: borde }} />}
            <span className={`inline-flex items-center ${chica ? 'gap-1 px-1.5' : 'gap-1.5 px-2'}`}>
              <Icono className={chica ? 'h-3 w-3' : 'h-4 w-4'} style={{ color: icono }} strokeWidth={2.25} aria-hidden="true" />
              {rotulo}
            </span>
          </Fragment>
        );
      })}
    </span>
  );
}

/**
 * Card de SECCIÓN — la que abre el popup con los modos de ese destino
 * (ADR-2026-09-29-la-home-se-parte-por-destino, supersede las cards "Crear imágenes" /
 * "Crear videos"). La pregunta ya no es "¿imagen o video?" sino "¿para dónde es?".
 *
 * Las tres van CLARAS y con la misma luminosidad, como las vertientes de antes: lo que
 * las distingue es el tinte y sus resultados, no el peso. La de "Anuncios" todavía no tiene
 * modos: se dibuja igual —anuncia lo que viene—, no es clickeable y lleva la pastilla
 * "Próximamente" donde las otras tienen la flecha.
 */
/** Los "Próximamente" de la sección que siguen en pie: sin los que ya están en `modos`. */
function proximosDe(seccion: Seccion, modos: readonly { path: string }[]) {
  return proximosQueQuedan(SECCIONES[seccion].modosProximos ?? [], modos.map((m) => m.path));
}

function CardSeccion({
  seccion,
  cantidad,
  rubro,
  apaisada = false,
  onElegir,
}: {
  seccion: Seccion;
  cantidad: number;
  /** Desde `lg`, 4:3 en vez de cuadrada: con dos cards por fila un cuadrado sería enorme. */
  apaisada?: boolean;
  /** El rubro de la marca: elige las fotos del abanico (`plataformasDe`). */
  rubro: string | null;
  onElegir: () => void;
}) {
  const copy = SECCIONES[seccion];
  const proximamente = copy.proximamente || cantidad === 0;
  return (
    <button
      type="button"
      onClick={proximamente ? undefined : onElegir}
      disabled={proximamente}
      // `aria-haspopup`: el botón no lleva a otra pantalla, abre un diálogo.
      aria-haspopup={proximamente ? undefined : 'dialog'}
      className={`group relative flex h-[268px] flex-col ${apaisada ? 'lg:aspect-[4/3]' : 'lg:aspect-square'} lg:h-auto lg:[container-type:inline-size] overflow-hidden rounded-2xl border border-black/[0.06] p-6 text-left transition-shadow disabled:cursor-default ${copy.tinte} ${
        proximamente ? '' : 'hover:shadow-[0_10px_36px_rgba(16,24,45,0.12)]'
      }`}
    >
      {/* Los formatos arriba, en una pastilla (ADR-2026-10-14-los-formatos-de-la-card-van-en-una-pastilla). */}
      <span className="block">
        <FormatosDeLaCard formatos={copy.formatos} seccion={seccion} />
      </span>

      <span className="flex flex-1 items-center justify-center">
        <AbanicoDeResultados plataformas={plataformasDe(seccion, rubro)} menor={apaisada} />
      </span>

      {/* Abajo, el destino y una bajada que arranca con "Creá", y a la derecha la flecha (o
          "Próximamente"), alineada con la última línea. */}
      <span className="flex items-end gap-3">
        <span className="min-w-0 flex-1">
          <span className="block text-[22px] font-bold leading-tight tracking-[-0.02em] text-brand-black/90">
            {copy.titulo}
          </span>
          <span className="mt-1 block text-[13.5px] leading-snug text-black/50">
            {copy.bajada}
          </span>
        </span>
        <span className="shrink-0">
          {proximamente ? (
            // La pastilla de `CardModoProximo` en el popup
            // (ADR-2026-10-07-anuncios-dice-proximamente), sin sombra: en la card, como la
            // flecha, va plana.
            <span className="inline-flex items-center rounded-full bg-white/90 px-3 py-1.5 text-[12px] font-bold text-brand-black ring-1 ring-black/[0.04] backdrop-blur">
              Próximamente
            </span>
          ) : (
            // Destaca poco a propósito: blanco con la flecha en tinta, sin el azul de marca,
            // que le ganaba al título. En el hover de la card se llena con el color fuerte de su sección y la flecha pasa a blanco.
            <span className={`grid h-8 w-10 place-items-center rounded-full bg-white text-black/75 ring-1 ring-black/[0.04] transition-[transform,background-color,color] duration-300 group-hover:translate-x-1 group-hover:text-white group-hover:ring-transparent ${copy.flechaHover}`}>
              <ArrowRight className="h-4 w-4" />
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

/**
 * El popup con los modos de una sección.
 *
 * Va por `createPortal` a `document.body` y no en su lugar del árbol: el shell envuelve
 * el contenido en contenedores con `transform` para el fade entre secciones, y un
 * `position: fixed` dentro de un ancestro transformado se posiciona contra ESE ancestro
 * en vez de contra el viewport — el overlay habría quedado corrido. Es el mismo motivo
 * por el que el menú de cuenta del shell también se portalea.
 */
function PopupPorSeccion({
  seccion,
  modos,
  rubroMarca,
  onAbrir,
  onCerrar,
}: {
  seccion: Seccion;
  modos: ModoHome[];
  rubroMarca: string | null;
  onAbrir: (m: ModoHome) => void;
  onCerrar: () => void;
}) {
  // Escape cierra. Se escucha en `document` y no en el diálogo para que funcione sin
  // depender de dónde quedó el foco al abrir.
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [onCerrar]);

  // Tres columnas en la computadora y el diálogo ancho para que las carátulas no se achiquen; dos
  // por debajo de `lg` (ADR-2026-10-15-los-popups-por-destino-en-tres-columnas, que supersede
  // ADR-2026-10-12-los-popups-por-destino-siempre-en-dos-columnas).
  // El modo principal (ADR-2026-10-31-el-modo-principal-de-cada-seccion); con candado no se destaca, y entonces
  // tampoco cambian las columnas.
  const candidato = seccion === 'anuncios' ? null : modoPrincipalDeLaSeccion(seccion === 'redes' ? 'redes' : 'catalogo', modos.map((m) => m.path));
  const principal = candidato && modos.some((m) => m.path === candidato && !m.bloqueado) ? candidato : null;

  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-label={SECCIONES[seccion].tituloPopup}
    >
      {/* El fondo cierra al click. Es un `button` y no un `div` con `onClick` para que
          ese cierre también esté al alcance del teclado. */}
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onCerrar}
        className="absolute inset-0 h-full w-full cursor-default bg-black/45 backdrop-blur-[2px]"
      />
      {/* `max-h-[85vh]` + scroll propio: con 7 modos la grilla mide tres filas y en
          una pantalla de 800px de alto el diálogo se salía por abajo. */}
      <div
        className="animate-fade-in relative z-10 max-h-[85vh] w-full max-w-[590px] overflow-y-auto lg:max-w-[880px] rounded-2xl bg-white p-6 shadow-[0_24px_80px_rgba(16,24,45,0.28)]"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-[22px] font-bold tracking-[-0.01em]">
              {SECCIONES[seccion].tituloPopup}
            </h2>
            <p className="mt-1 text-[13.5px] text-black/50">
              Elegí la herramienta con la que querés empezar.
            </p>
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl text-black/50 transition-colors hover:bg-black/[0.05] hover:text-black/80"
          >
            <X className="h-[18px] w-[18px]" />
          </button>
        </div>

        <GrillaDeModos
          // Esta home es la interfaz nueva: "Recrear", no "Plantillas"
          // (ADR-2026-10-07-plantillas-en-la-vieja-recrear-en-la-nueva).
          uiNueva
          modos={modos}
          rubroMarca={rubroMarca}
          onAbrir={onAbrir}
          // Una herramienta sola ocupa todo el ancho y dos van de a dos (#184); con tres o más,
          // tres columnas en la compu (ADR-2026-10-15-los-popups-por-destino-en-tres-columnas).
          className={`mt-5 grid gap-3 ${columnasDelPopup(modos.length + proximosDe(seccion, modos).length, !!principal)}`}
          // El modo principal de la sección, al doble de ancho y con los demás al costado desde `lg`
          // (ADR-2026-10-31-el-modo-principal-de-cada-seccion).
          destacado={principal}
          destacadoAlCostado
          caratulaPropia={(path) =>
            portadaDeRubro(path, rubroMarca) &&
            !((seccion === 'redes' || seccion === 'catalogos') &&
              tieneCaratulaDelRubro(seccion === 'redes' ? 'redes' : 'catalogo', path, rubroMarca))
              ? undefined
              : // Con la excepción del rubro (Belleza ve sus propias fotos en Redes y Catálogos).
                seccion === 'redes' || seccion === 'catalogos'
                ? caratulaConTinte(seccion === 'redes' ? 'redes' : 'catalogo', path, rubroMarca)
                : SECCIONES[seccion].caratulas?.[path]
          }
          alFinal={proximosDe(seccion, modos).map((p) => (
            <CardModoProximo key={p.nombre} nombre={p.nombre} bajada={p.bajada} />
          ))}
        />
      </div>
    </div>,
    document.body,
  );
}

export function CabeceraPorDestino({
  modos,
  rubroMarca,
  abrirModo,
  irA,
}: {
  modos: ModoHome[];
  rubroMarca: string | null;
  /** Abrir un modo (candado → ejemplos o planes; si no, el modo con el fade del shell). */
  abrirModo: (m: ModoHome) => void;
  /** Navegar a una ruta con el fade del shell (para una sección con `ruta`). */
  irA: (ruta: string) => void;
}) {
  /** Qué popup está abierto. `null` = ninguno, que es como abre la pantalla. */
  const [popup, setPopup] = useState<Seccion | null>(null);

  const navigate = useNavigate();
  const porSeccion = useMemo<Record<Seccion, ModoHome[]>>(() => {
    const visibles = modos.filter((m) => !MODOS_OCULTOS_EN_LA_HOME.has(m.path));
    // En el orden de la lista compartida, una vez cada uno.
    const enOrden = (paths: readonly string[]) =>
      paths.flatMap((path) => {
        const m = visibles.find((x) => x.path === path);
        return m ? [m] : [];
      });
    // Los rubros no principales, sólo el Catálogo con el agente
    // (ADR-2026-10-26-catalogo-con-el-agente-para-los-rubros-no-principales).
    const catalogos = enOrden(modosDeCatalogoDelRubro(rubroMarca));
    // Crear y Recrear, una vez cada uno (Plantillas llega dos veces, una por familia: se
    // toma la primera, la de imagen). Un rubro sin plantillas tiene Replicar imagen en el
    // lugar de Recrear (ADR-2026-10-12-replicar-imagen-en-lugar-de-recrear).
    const [crear, ...recrear] = modosDeRedesDelRubro((p) => visibles.some((x) => x.path === p)).flatMap((path) => {
      const m = visibles.find((x) => x.path === path);
      return m ? [m] : [];
    });
    // "Imagen automática" (ADR-2026-10-12-imagen-automatica-en-el-popup-de-redes): en la
    // interfaz nueva vive ADENTRO de Crear (la tarjeta Modo de la caja), así que la card abre
    // Crear con ese modo ya elegido. Va con el candado de Crear y con la carátula del modo
    // Automático del catálogo (`auto`: su foto, su video y la portada de cada rubro).
    // Abre el modo Automático PROPIAMENTE DICHO (su pantalla, con su candado), no Crear: así
    // lo pidió Maxi (ADR-2026-10-12-imagen-automatica-abre-su-modo). Sólo si la lista no lo
    // trae, queda el camino de antes por Crear.
    const autoDelCatalogo = visibles.find((x) => x.path === 'auto');
    const automatica: ModoHome | null = autoDelCatalogo
      ? { ...autoDelCatalogo, titulo: 'Imagen automática' }
      : crear && crear.path === 'redes'
        ? {
            ...crear,
            path: 'auto',
            titulo: 'Imagen automática',
            onAbrir: crear.bloqueado ? crear.onAbrir : () => navigate(RUTA_REDES, { state: { modo: 'automatico' } }),
          }
        : null;
    // El orden: Imagen automática, Recrear y Crear desde cero. Un rubro sin plantillas se queda
    // con las otras dos.
    const redes = automatica ? [automatica, ...recrear, crear] : [crear, ...recrear].filter(Boolean);
    // "Anuncios" todavía no tiene modos: la card sale como "Próximamente".
    return { redes, catalogos, anuncios: [] };
  }, [modos, navigate, rubroMarca]);

  // El popup se cierra ANTES de navegar: el fade de salida del shell apaga el
  // contenido de la home, y el overlay —que cuelga de `body`— no se enteraría.
  const abrir = (m: ModoHome) => {
    setPopup(null);
    abrirModo(m);
  };

  return (
    <>
      {/* ── Secciones ───────────────────────────────────────────────────────
          Tres cards por DESTINO —redes, catálogos, anuncios— que abren el popup con
          los modos de esa sección (ADR-2026-09-29-la-home-se-parte-por-destino). El
          Agente ya no vive en la home de escritorio: ni la barra que lo presentaba ni
          el hero que la reemplazaba en los rubros sin catálogo propio. */}
      <section className="mt-8">
        {/* Sin título visible: arriba ya está el saludo y las cards se explican solas, así que
            "Elegí qué querés crear" se leía como un segundo título. Queda para lectores de
            pantalla, que necesitan el nombre de la sección. */}
        <h2 className="sr-only">Elegí qué querés crear</h2>
        {/* Tres columnas desde `lg`: más angosto, con la sidebar al lado, cada card
            quedaba de ~200px y el abanico de logos no entraba. Desde `lg` las cards son
            CUADRADAS (`aspect-square` en `CardSeccion`); apiladas en una columna, un
            cuadrado del ancho entero sería enorme, así que ahí quedan en 268px. */}
        {/* Con "Anuncios" oculto (ADR-2026-10-26-anuncios-se-oculta-hasta-tener-modos) quedan
            dos: van de a dos y apaisadas, 4:3, que es lo más bajo en lo que entra el abanico
            (crece con el ancho de la card). */}
        <div className={`grid grid-cols-1 gap-2.5 ${DESTINOS_VISIBLES.length === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
          {DESTINOS_VISIBLES.map((s) =>
            porSeccion[s].length === 0 && !SECCIONES[s].proximamente && !SECCIONES[s].ruta ? null : (
              <CardSeccion
                key={s}
                seccion={s}
                cantidad={porSeccion[s].length}
                rubro={rubroMarca}
                apaisada={DESTINOS_VISIBLES.length < 3}
                onElegir={() => {
                  const ruta = SECCIONES[s].ruta;
                  if (ruta) irA(ruta);
                  // Con una sola opción no hay nada que elegir: se abre directo.
                  else if (porSeccion[s].length === 1 && !proximosDe(s, porSeccion[s]).length) abrir(porSeccion[s][0]);
                  else setPopup(s);
                }}
              />
            ),
          )}
        </div>
      </section>

      {popup && (
        <PopupPorSeccion
          seccion={popup}
          modos={porSeccion[popup]}
          rubroMarca={rubroMarca}
          onAbrir={abrir}
          onCerrar={() => setPopup(null)}
        />
      )}
    </>
  );
}
